import axios from "axios";
import {create} from "zustand";
import type {
  ICreditAllowedActions,
  ICreditCancelEffective,
  ICreditMonthlyPlanId,
  ICreditNextAction,
  ICreditPolarCheckoutNextAction,
  ICreditPurchase,
  ICreditRefundQuote,
  ICreditRefundResponse,
  ICreditSubscription,
  ICreditSubscriptionChangeResponse,
  ICreditUpgradeQuote,
  ICreditsBalance,
  ICreditsCatalog,
  ICreditPurchaseResponse,
} from "../models/interfaces/credits.interface.ts";
import {
  cancelCreditSubscription,
  changeNextCreditPlan,
  createCreditsIdempotencyKey,
  getCreditPurchase,
  getCreditRefundQuote,
  getCreditsBalance,
  getCreditsCatalog,
  getCreditSubscription,
  getCreditUpgradeQuote,
  purchaseCreditPackage,
  requestCreditRefund,
  resumeCreditSubscription,
  upgradeCreditSubscription,
} from "../services/creditsService.ts";
import useUserStore from "./useUserStore.ts";

// allow: SIZE_OK — one cohesive workspace-fenced Zustand billing state machine.

interface INotEnoughCredits {
  requiredCredits: number;
  availableCredits: number;
}

type BillingActionType = "purchase" | "upgrade" | "change_next_plan" | "cancel" | "resume" | "refund";

export type PricingView = "plans" | "billing";

interface IPendingBillingAction {
  type: BillingActionType;
  workspaceId: string;
  generation: number;
  sequence: number;
  idempotencyKey: string;
  bodyKey: string;
}

// A Polar checkout keeps the idempotency key of the purchase/upgrade that opened it, so re-submitting the same
// action replays the same checkout session instead of opening a second one. A terminal result retires the key.
export interface ICreditCheckout {
  action: "purchase" | "upgrade";
  url: string;
  expiresAt: string;
  purchaseId: string;
  idempotencyKey: string;
  status: "open" | "fulfilled" | "failed" | "timed_out";
}

export const CHECKOUT_POLL_INTERVAL_MS = 2_000;
export const CHECKOUT_POLL_TIMEOUT_MS = 180_000;
// After the 3-minute wait gives up, a paid order is usually waiting on a delayed webhook or on the backend's
// reconciliation (seen granting ~7 minutes after payment), so the follow-up checks slowly and stops after 15 minutes.
export const CHECKOUT_FOLLOW_INTERVAL_MS = 20_000;
export const CHECKOUT_FOLLOW_TIMEOUT_MS = 15 * 60_000;

interface IRequestContext {
  workspaceId: string;
  generation: number;
  sequence: number;
}

interface ICreditsState {
  workspaceId: string | null;
  generation: number;
  lastAppliedSequence: number;
  balance: ICreditsBalance | null;
  subscription: ICreditSubscription | null;
  allowedActions: ICreditAllowedActions | null;
  upgradeQuote: ICreditUpgradeQuote | null;
  refundQuote: ICreditRefundQuote | null;
  nextAction: ICreditNextAction | null;
  checkout: ICreditCheckout | null;
  pendingAction: IPendingBillingAction | null;
  pendingActions: Record<string, IPendingBillingAction>;
  catalog: ICreditsCatalog | null;
  status: "idle" | "loading" | "loaded" | "error";
  isPricingOpen: boolean;
  pricingView: PricingView;
  notEnough: INotEnoughCredits | null;
  bindWorkspace: (workspaceId: string) => void;
  fetchBalance: (workspaceId: string) => Promise<ICreditsBalance | null>;
  fetchCatalog: () => Promise<void>;
  fetchSubscription: (workspaceId: string) => Promise<ICreditSubscription | null>;
  fetchUpgradeQuote: (workspaceId: string, planId: ICreditMonthlyPlanId) => Promise<ICreditUpgradeQuote | null>;
  fetchRefundQuote: (workspaceId: string, purchaseId: string, credits: number) => Promise<ICreditRefundQuote | null>;
  purchasePackage: (workspaceId: string, packageId: string) => Promise<ICreditPurchaseResponse | null>;
  upgradeSubscription: (
    workspaceId: string,
    planId: ICreditMonthlyPlanId,
    expectedVersion: number,
  ) => Promise<ICreditSubscriptionChangeResponse | null>;
  changeNextPlan: (
    workspaceId: string,
    planId: ICreditMonthlyPlanId,
    expectedVersion: number,
  ) => Promise<ICreditSubscriptionChangeResponse | null>;
  cancelSubscription: (workspaceId: string, effective: ICreditCancelEffective, expectedVersion: number) => Promise<ICreditSubscriptionChangeResponse | null>;
  resumeSubscription: (workspaceId: string, expectedVersion: number) => Promise<ICreditSubscriptionChangeResponse | null>;
  requestRefund: (workspaceId: string, purchaseId: string, credits: number, expectedVersion: number) => Promise<ICreditRefundResponse | null>;
  // Polls the purchase every 2 s for at most 3 min until it is fulfilled or failed, then refreshes the balance.
  // Resolves null when cancelled or superseded, and the still-pending purchase on timeout.
  awaitCheckoutResult: (purchaseId: string) => Promise<ICreditPurchase | null>;
  // Keeps checking a timed-out checkout every 20 s for at most 15 min; settles it and refreshes the balance once the
  // purchase is fulfilled or failed. Resolves that purchase, or null when cancelled, superseded or out of time.
  followCheckoutResult: (purchaseId: string) => Promise<ICreditPurchase | null>;
  // Stops awaitCheckoutResult or followCheckoutResult, whichever is running.
  cancelCheckoutResult: () => void;
  openPricing: (view?: PricingView) => void;
  setPricingView: (view: PricingView) => void;
  closePricing: () => void;
  openNotEnough: (payload: INotEnoughCredits) => void;
  closeNotEnough: () => void;
  reset: () => void;
}

let requestGeneration = 0;
let requestSequence = 0;
let checkoutWait: AbortController | null = null;

const stopCheckoutWait = (): void => {
  checkoutWait?.abort();
  checkoutWait = null;
};

// Resolves true after `ms`, or false as soon as the wait is cancelled.
const pollDelay = (ms: number, signal: AbortSignal): Promise<boolean> => new Promise((resolve) => {
  if (signal.aborted) {
    resolve(false);
    return;
  }
  const onAbort = (): void => {
    clearTimeout(timer);
    resolve(false);
  };
  const timer = setTimeout(() => {
    signal.removeEventListener("abort", onAbort);
    resolve(true);
  }, ms);
  signal.addEventListener("abort", onAbort, {once: true});
});

const polarCheckoutOf = (
  result: ICreditPurchaseResponse | ICreditSubscriptionChangeResponse | ICreditRefundResponse,
): ICreditPolarCheckoutNextAction | null =>
  "nextAction" in result && result.nextAction?.type === "polar_checkout" ? result.nextAction : null;

const workspaceState = {
  workspaceId: null,
  generation: 0,
  lastAppliedSequence: 0,
  balance: null,
  subscription: null,
  allowedActions: null,
  upgradeQuote: null,
  refundQuote: null,
  nextAction: null,
  checkout: null,
  pendingAction: null,
  pendingActions: {},
  notEnough: null,
};

const initialState = {
  ...workspaceState,
  catalog: null,
  status: "idle" as const,
  isPricingOpen: false,
  pricingView: "plans" as const,
};

// No response, a timeout or a 5xx can follow a commit, so the outcome is unknown: the action keeps its key for a
// same-key replay (C7), and the UI must show the result as unconfirmed, never as failed.
export const isAmbiguousResult = (error: unknown): boolean =>
  axios.isAxiosError(error)
  && (error.response === undefined || error.response.status >= 500);

export const useCreditsStore = create<ICreditsState>((set, get) => {
  const bindWorkspace = (workspaceId: string): void => {
    if (get().workspaceId === workspaceId) return;
    stopCheckoutWait();
    requestGeneration += 1;
    set({...workspaceState, workspaceId, generation: requestGeneration, status: "idle"});
  };

  const beginRequest = (workspaceId: string): IRequestContext => {
    bindWorkspace(workspaceId);
    requestSequence += 1;
    return {workspaceId, generation: requestGeneration, sequence: requestSequence};
  };

  const isCurrent = ({workspaceId, generation, sequence}: IRequestContext): boolean => {
    const state = get();
    return state.workspaceId === workspaceId
      && state.generation === generation
      && sequence >= state.lastAppliedSequence;
  };

  const applyBalance = (context: IRequestContext, balance: ICreditsBalance): boolean => {
    if (!isCurrent(context)) return false;
    set({
      balance,
      subscription: balance.subscription,
      allowedActions: balance.allowedActions,
      status: "loaded",
      lastAppliedSequence: context.sequence,
    });
    return true;
  };

  const pendingAction = (
    context: IRequestContext,
    type: BillingActionType,
    body: object,
  ): IPendingBillingAction => {
    const bodyKey = JSON.stringify(body);
    // Each submitted action keeps its own key until it resolves definitely, so submitting a different action
    // never costs an unknown-result action its replay key (C7). Workspace switch and reset clear the map.
    const current = get().pendingActions[`${type}:${bodyKey}`];
    const idempotencyKey = current?.workspaceId === context.workspaceId
      && current.generation === context.generation
      ? current.idempotencyKey
      : createCreditsIdempotencyKey();
    const pending = {...context, type, idempotencyKey, bodyKey};
    set((state) => ({
      pendingAction: pending,
      pendingActions: {...state.pendingActions, [`${type}:${bodyKey}`]: pending},
      status: "loading",
    }));
    return pending;
  };

  const ownsKey = (current: IPendingBillingAction | null | undefined, pending: IPendingBillingAction): boolean =>
    current?.sequence === pending.sequence && current.idempotencyKey === pending.idempotencyKey;

  // Retires only this action's key; a newer submission of the same or another action keeps its own.
  const retireKey = (pending: IPendingBillingAction): void => {
    const state = get();
    const identity = `${pending.type}:${pending.bodyKey}`;
    const pendingActions = {...state.pendingActions};
    if (ownsKey(pendingActions[identity], pending)) delete pendingActions[identity];
    set({
      pendingActions,
      pendingAction: ownsKey(state.pendingAction, pending) ? null : state.pendingAction,
    });
  };

  const finishMutation = <T extends ICreditPurchaseResponse | ICreditSubscriptionChangeResponse | ICreditRefundResponse>(
    context: IRequestContext,
    pending: IPendingBillingAction,
    result: T,
  ): T | null => {
    // The server consumed this key even if its balance is now stale, so a new submission must mint a fresh one;
    // a newer in-flight action keeps its own key (C7).
    const state = get();
    const belongsToCurrentWorkspace = state.workspaceId === context.workspaceId
      && state.generation === context.generation;
    const polarCheckout = polarCheckoutOf(result);
    if (polarCheckout) {
      // Nothing is committed until the checkout is paid: the key stays for a retry that must return the same session.
      if (ownsKey(state.pendingAction, pending)) set({pendingAction: null});
    } else {
      retireKey(pending);
    }
    if (!belongsToCurrentWorkspace) return null;
    if (polarCheckout) {
      // A different checkout supersedes whatever wait or follow-up is still checking the previous one.
      if (get().checkout?.purchaseId !== polarCheckout.purchaseId) stopCheckoutWait();
      set({
        checkout: {
          action: pending.type === "upgrade" ? "upgrade" : "purchase",
          url: polarCheckout.url,
          expiresAt: polarCheckout.expiresAt,
          purchaseId: polarCheckout.purchaseId,
          idempotencyKey: pending.idempotencyKey,
          status: "open",
        },
      });
    }
    if (applyBalance(context, result.balance)) {
      set({
        subscription: "subscription" in result ? result.subscription : result.balance.subscription,
        nextAction: "nextAction" in result ? result.nextAction ?? null : null,
      });
    }
    return result;
  };

  const failMutation = (context: IRequestContext, pending: IPendingBillingAction, error: unknown): null => {
    const state = get();
    // A workspace switch or logout discards the failure outright: its UI is already gone.
    if (state.workspaceId !== context.workspaceId || state.generation !== context.generation) return null;
    const current = isCurrent(context);
    if (current) set({status: "error"});
    // A definite failure retires only the key this action submitted; a newer in-flight action keeps its key
    // for an unknown-result retry (C7), and an unknown result keeps its own key for replay.
    if (!isAmbiguousResult(error)) {
      retireKey(pending);
    } else if (!current && ownsKey(state.pendingAction, pending)) {
      set({pendingAction: null});
    }
    // Superseded or not, the caller must learn the mutation failed; a null would read as success (C7).
    throw error;
  };

  const settleCheckout = (purchaseId: string, status: ICreditCheckout["status"]): void => {
    const {checkout, pendingActions} = get();
    if (checkout?.purchaseId !== purchaseId) return;
    if (status === "timed_out") {
      set({checkout: {...checkout, status}});
      return;
    }
    // A paid or failed checkout consumed its key; the next submission must open a new session.
    const remaining = Object.fromEntries(
      Object.entries(pendingActions).filter(([, row]) => row.idempotencyKey !== checkout.idempotencyKey),
    );
    set({checkout: {...checkout, status}, pendingActions: remaining});
  };

  const awaitCheckoutResult = async (purchaseId: string): Promise<ICreditPurchase | null> => {
    const {workspaceId, generation} = get();
    if (!workspaceId) return null;
    stopCheckoutWait();
    const controller = new AbortController();
    checkoutWait = controller;
    const owned = (): boolean => !controller.signal.aborted
      && get().workspaceId === workspaceId
      && get().generation === generation;
    // The deadline is absolute: a held poll or balance refresh cannot extend it, and cancel settles at once.
    const stop = new AbortController();
    let timedOut = false;
    const deadlineTimer = setTimeout(() => {
      timedOut = true;
      stop.abort();
    }, CHECKOUT_POLL_TIMEOUT_MS);
    const onCancel = (): void => stop.abort();
    controller.signal.addEventListener("abort", onCancel, {once: true});
    const stopped = new Promise<null>((resolve) => {
      stop.signal.addEventListener("abort", () => resolve(null), {once: true});
    });
    let latest: ICreditPurchase | null = null;
    try {
      while (owned()) {
        const request = new AbortController();
        // A failed poll says nothing about the payment; the next poll (until the deadline) decides.
        const polled = await Promise.race([
          getCreditPurchase(workspaceId, purchaseId, request.signal).catch(() => null),
          stopped,
        ]);
        if (stop.signal.aborted) request.abort();
        if (!owned()) return null;
        if (timedOut) break;
        latest = polled ?? latest;
        if (latest?.status === "fulfilled" || latest?.status === "failed") {
          settleCheckout(purchaseId, latest.status);
          await Promise.race([get().fetchBalance(workspaceId), stopped]);
          return latest;
        }
        await pollDelay(CHECKOUT_POLL_INTERVAL_MS, stop.signal);
        if (timedOut) break;
      }
      if (!owned()) return null;
      settleCheckout(purchaseId, "timed_out");
      return latest;
    } finally {
      clearTimeout(deadlineTimer);
      controller.signal.removeEventListener("abort", onCancel);
      if (checkoutWait === controller) checkoutWait = null;
    }
  };

  const followCheckoutResult = async (purchaseId: string): Promise<ICreditPurchase | null> => {
    const {workspaceId, generation} = get();
    if (!workspaceId) return null;
    stopCheckoutWait();
    const controller = new AbortController();
    checkoutWait = controller;
    const owned = (): boolean => {
      const state = get();
      return !controller.signal.aborted
        && state.workspaceId === workspaceId
        && state.generation === generation
        && state.checkout?.purchaseId === purchaseId
        && state.checkout.status === "timed_out";
    };
    // The deadline aborts like a cancel, so a held check or balance refresh cannot outlive it.
    const deadlineTimer = setTimeout(() => controller.abort(), CHECKOUT_FOLLOW_TIMEOUT_MS);
    const stopped = new Promise<void>((resolve) => {
      controller.signal.addEventListener("abort", () => resolve(), {once: true});
    });
    try {
      while (owned()) {
        if (!await pollDelay(CHECKOUT_FOLLOW_INTERVAL_MS, controller.signal) || !owned()) return null;
        // A failed check says nothing about the payment; the next one decides.
        const polled = await getCreditPurchase(workspaceId, purchaseId, controller.signal).catch(() => null);
        if (!owned()) return null;
        if (polled?.status === "fulfilled" || polled?.status === "failed") {
          settleCheckout(purchaseId, polled.status);
          // The store's request fences still decide whether a late balance response applies.
          await Promise.race([get().fetchBalance(workspaceId), stopped]);
          return controller.signal.aborted ? null : polled;
        }
      }
      return null;
    } finally {
      clearTimeout(deadlineTimer);
      if (checkoutWait === controller) checkoutWait = null;
    }
  };

  return {
    ...initialState,
    bindWorkspace,
    awaitCheckoutResult,
    followCheckoutResult,
    cancelCheckoutResult: stopCheckoutWait,
    fetchBalance: async (workspaceId) => {
      const context = beginRequest(workspaceId);
      set({status: "loading"});
      try {
        const balance = await getCreditsBalance(workspaceId);
        return applyBalance(context, balance) ? balance : null;
      } catch {
        if (isCurrent(context)) set({status: "error"});
        return null;
      }
    },
    fetchCatalog: async () => {
      const generation = requestGeneration;
      set({status: "loading"});
      try {
        const catalog = await getCreditsCatalog();
        if (generation === requestGeneration) set({catalog, status: "loaded"});
      } catch {
        if (generation === requestGeneration) set({status: "error"});
      }
    },
    fetchSubscription: async (workspaceId) => {
      const context = beginRequest(workspaceId);
      set({status: "loading"});
      try {
        const subscription = await getCreditSubscription(workspaceId);
        if (!isCurrent(context)) return null;
        set({subscription, status: "loaded", lastAppliedSequence: context.sequence});
        return subscription;
      } catch {
        if (isCurrent(context)) set({status: "error"});
        return null;
      }
    },
    fetchUpgradeQuote: async (workspaceId, planId) => {
      const context = beginRequest(workspaceId);
      set({status: "loading", upgradeQuote: null});
      try {
        const quote = await getCreditUpgradeQuote(workspaceId, planId);
        if (!isCurrent(context)) return null;
        set({upgradeQuote: quote, status: "loaded", lastAppliedSequence: context.sequence});
        return quote;
      } catch {
        if (isCurrent(context)) set({status: "error"});
        return null;
      }
    },
    fetchRefundQuote: async (workspaceId, purchaseId, credits) => {
      const context = beginRequest(workspaceId);
      set({status: "loading", refundQuote: null});
      try {
        const quote = await getCreditRefundQuote(workspaceId, purchaseId, credits);
        if (!isCurrent(context)) return null;
        set({refundQuote: quote, status: "loaded", lastAppliedSequence: context.sequence});
        return quote;
      } catch {
        if (isCurrent(context)) set({status: "error"});
        return null;
      }
    },
    purchasePackage: async (workspaceId, packageId) => {
      const context = beginRequest(workspaceId);
      const pending = pendingAction(context, "purchase", {packageId});
      try {
        const result = await purchaseCreditPackage(workspaceId, packageId, pending.idempotencyKey);
        return finishMutation(context, pending, result);
      } catch (error) {
        return failMutation(context, pending, error);
      }
    },
    upgradeSubscription: async (workspaceId, planId, expectedVersion) => {
      const context = beginRequest(workspaceId);
      // The target is part of the action's identity: a different target is a different idempotent action.
      const pending = pendingAction(context, "upgrade", {planId, expectedVersion});
      try {
        const result = await upgradeCreditSubscription(workspaceId, planId, expectedVersion, pending.idempotencyKey);
        return finishMutation(context, pending, result);
      } catch (error) {
        return failMutation(context, pending, error);
      }
    },
    changeNextPlan: async (workspaceId, planId, expectedVersion) => {
      const context = beginRequest(workspaceId);
      const pending = pendingAction(context, "change_next_plan", {planId, expectedVersion});
      try {
        const result = await changeNextCreditPlan(workspaceId, planId, expectedVersion, pending.idempotencyKey);
        return finishMutation(context, pending, result);
      } catch (error) {
        return failMutation(context, pending, error);
      }
    },
    cancelSubscription: async (workspaceId, effective, expectedVersion) => {
      const context = beginRequest(workspaceId);
      const pending = pendingAction(context, "cancel", {effective, expectedVersion});
      try {
        const result = await cancelCreditSubscription(
          workspaceId,
          effective,
          expectedVersion,
          pending.idempotencyKey,
        );
        return finishMutation(context, pending, result);
      } catch (error) {
        return failMutation(context, pending, error);
      }
    },
    resumeSubscription: async (workspaceId, expectedVersion) => {
      const context = beginRequest(workspaceId);
      const pending = pendingAction(context, "resume", {expectedVersion});
      try {
        const result = await resumeCreditSubscription(workspaceId, expectedVersion, pending.idempotencyKey);
        return finishMutation(context, pending, result);
      } catch (error) {
        return failMutation(context, pending, error);
      }
    },
    requestRefund: async (workspaceId, purchaseId, credits, expectedVersion) => {
      const context = beginRequest(workspaceId);
      const body = {purchaseId, credits, expectedVersion};
      const pending = pendingAction(context, "refund", body);
      try {
        const result = await requestCreditRefund(
          workspaceId,
          purchaseId,
          credits,
          expectedVersion,
          pending.idempotencyKey,
        );
        return finishMutation(context, pending, result);
      } catch (error) {
        return failMutation(context, pending, error);
      }
    },
    openPricing: (view = "plans") => set({isPricingOpen: true, pricingView: view}),
    setPricingView: (view) => set({pricingView: view}),
    closePricing: () => set({isPricingOpen: false}),
    openNotEnough: (payload) => {
      const workspaceId = useUserStore.getState().user?.workspaceId;
      if (workspaceId) bindWorkspace(workspaceId);
      set({notEnough: payload});
      if (workspaceId) void get().fetchBalance(workspaceId);
    },
    closeNotEnough: () => set({notEnough: null}),
    reset: () => {
      stopCheckoutWait();
      requestGeneration += 1;
      set({...initialState, generation: requestGeneration});
    },
  };
});
